/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   dateFormatter.ts
 *     Feature         :   117-operational-dashboards
 *     Description     :   UTC timestamp formatting utilities
 *     Constitution    :   v1.12.0
 *     Author          :   Peter Dobler
 *     Created         :   2026-02-05
 * =========================================================================================
 */

/** Canonical display timezone — every stored timestamp is UTC; we present it in US Eastern. */
export const DISPLAY_TIME_ZONE = 'America/New_York';

/**
 * Format a UTC timestamp for display in US Eastern (EST/EDT auto-resolved), e.g.
 * "Jul 13, 2026, 6:40 PM EDT". This is the app-standard timestamp formatter — prefer it
 * over ad-hoc toLocaleString()/toLocaleDateString() so timestamps read consistently in
 * Eastern regardless of the viewer's browser timezone.
 */
export function formatEastern(date: string | Date | null | undefined): string {
  if (!date) return 'N/A';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return 'Invalid date';
  return d.toLocaleString('en-US', {
    timeZone: DISPLAY_TIME_ZONE,
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short',
  });
}

/** Date-only display in US Eastern, e.g. "Jul 13, 2026". */
export function formatEasternDate(date: string | Date | null | undefined): string {
  if (!date) return 'N/A';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return 'Invalid date';
  return d.toLocaleDateString('en-US', {
    timeZone: DISPLAY_TIME_ZONE, year: 'numeric', month: 'short', day: 'numeric',
  });
}

/** Time-only display in US Eastern, e.g. "6:40 PM EDT". */
export function formatEasternTime(date: string | Date | null | undefined): string {
  if (!date) return 'N/A';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return 'Invalid date';
  return d.toLocaleTimeString('en-US', {
    timeZone: DISPLAY_TIME_ZONE, hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short',
  });
}

/**
 * Format a date as UTC with timezone indicator
 */
export function formatUTC(date: string | Date | null | undefined): string {
  if (!date) return 'N/A';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return 'Invalid date';
  return d.toLocaleString('en-US', { timeZone: 'UTC' }) + ' UTC';
}

/**
 * Format a date as a short date string (YYYY-MM-DD)
 */
export function formatShortDate(date: string | Date | null | undefined): string {
  if (!date) return 'N/A';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return 'Invalid date';
  return d.toISOString().split('T')[0];
}

/**
 * Format a date as relative time (e.g., "2 hours ago")
 */
export function formatRelative(date: string | Date | null | undefined): string {
  if (!date) return 'N/A';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return 'Invalid date';

  const now = Date.now();
  const diffMs = now - d.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 30) return `${diffDay}d ago`;
  return formatShortDate(d);
}

/**
 * Check if a date is stale (older than threshold minutes)
 */
export function isStale(date: string | Date | null | undefined, thresholdMinutes = 30): boolean {
  if (!date) return false;
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return false;
  return (Date.now() - d.getTime()) > thresholdMinutes * 60 * 1000;
}
